import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Text } from "@mantine/core";
import { isAxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";

import { userService } from "@/app/api/user";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import type { Nullable } from "@/types/utils";
import { buildFileDisplayName } from "@/utils/file-display-name";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

enum FileDownloadStatus {
  Idle = "idle",
  Loading = "loading",
  Pending = "pending",
  Failed = "failed",
}

interface FileDownloadLinkProps {
  candidateName: Nullable<string>;
  createdAt: string;
  internalLabel: string;
  jobTitle: string;
  questionRootId: string;
  s3Uri: string;
  sessionId: string;
}

export const FileDownloadLink = ({
  candidateName,
  createdAt,
  internalLabel,
  jobTitle,
  questionRootId,
  s3Uri,
  sessionId,
}: FileDownloadLinkProps) => {
  const track = useTrackAnalytics({ session_id: sessionId });
  const [status, setStatus] = useState<FileDownloadStatus>(
    FileDownloadStatus.Idle,
  );

  const displayName = useMemo(
    () =>
      buildFileDisplayName(
        candidateName,
        jobTitle,
        createdAt,
        internalLabel,
        s3Uri,
      ),
    [candidateName, createdAt, internalLabel, jobTitle, s3Uri],
  );

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setStatus(FileDownloadStatus.Loading);
      track("File download clicked", { question_root_id: questionRootId });

      // Open window synchronously to preserve user-gesture context — async
      // window.open calls are silently blocked by popup blockers in Safari.
      const newWindow = window.open("", "_blank");

      try {
        const response = await userService.axiosWithUserAuth.get<{
          downloadUrl: string;
        }>(
          `/screener-session/${sessionId}/file/${questionRootId}/download-url`,
        );

        // 202 resolves normally in Axios (2xx) — check before treating as download
        if (response.status === 202) {
          newWindow?.close();
          setStatus(FileDownloadStatus.Pending);
          return;
        }

        if (newWindow) {
          newWindow.location.href = response.data.downloadUrl;
        }
        setStatus(FileDownloadStatus.Idle);
      } catch (error) {
        newWindow?.close();
        const errorStatus = isAxiosError(error)
          ? error.response?.status
          : undefined;

        if (errorStatus === 403) {
          setStatus(FileDownloadStatus.Failed);
        } else {
          showUpwageNotification(
            "Failed to download file. Please try again.",
            NotificationTypes.Error,
          );
          setStatus(FileDownloadStatus.Idle);
        }
      }
    },
    [questionRootId, sessionId, track],
  );

  if (status === FileDownloadStatus.Pending) {
    return (
      <Text c="gray.6" data-testid="file-upload-response" size="sm">
        Scanning for security...
      </Text>
    );
  }

  if (status === FileDownloadStatus.Failed) {
    return (
      <Text c="gray.6" data-testid="file-upload-response" size="sm">
        File upload failed
      </Text>
    );
  }

  return (
    <Button
      className="max-w-fit px-0 font-normal"
      data-testid="file-upload-response"
      loading={status === FileDownloadStatus.Loading}
      onClick={handleClick}
      rightSection={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
      size="compact-sm"
      variant="transparent"
    >
      {displayName}
    </Button>
  );
};
